import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Video,
  Plus,
  Phone,
  Calendar as CalendarIcon,
  Clock,
  User,
  Play,
  Square,
  ExternalLink,
  Copy,
  CheckCircle,
  XCircle
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

export default function TeleAudiologyPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [sessions, setSessions] = useState([]);
  const [patients, setPatients] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeSession, setActiveSession] = useState(null);
  
  const [newSession, setNewSession] = useState({
    patient_id: "",
    clinic_id: "",
    session_type: "consultation",
    scheduled_at: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sessionsRes, patientsRes, clinicsRes] = await Promise.all([
        apiClient.get("/tele-sessions"),
        apiClient.get("/patients"),
        apiClient.get("/clinics")
      ]);
      setSessions(sessionsRes.data);
      setPatients(patientsRes.data);
      setClinics(clinicsRes.data);
      if (clinicsRes.data.length > 0) {
        setNewSession(prev => ({ ...prev, clinic_id: clinicsRes.data[0].id }));
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load tele-audiology sessions");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newSession,
        scheduled_at: selectedDate.toISOString()
      };
      const response = await apiClient.post("/tele-sessions", payload);
      setSessions([response.data, ...sessions]);
      toast.success("Tele-audiology session scheduled!");
      setDialogOpen(false);
      setNewSession({
        patient_id: "",
        clinic_id: clinics[0]?.id || "",
        session_type: "consultation",
        scheduled_at: "",
        notes: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create session");
    }
  };

  const startSession = async (sessionId) => {
    try {
      const response = await apiClient.post(`/tele-sessions/${sessionId}/start`);
      setSessions(sessions.map(s => 
        s.id === sessionId ? { ...s, status: "in_progress" } : s
      ));
      const session = sessions.find(s => s.id === sessionId);
      setActiveSession(session);
      
      // Show Jitsi Meet info
      toast.success("Jitsi Meet session started! Opening video room...", {
        description: "Patient will receive join link via WhatsApp"
      });
      
      // Open Jitsi Meet in new tab
      window.open(response.data.join_url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error("Failed to start session");
    }
  };

  const endSession = async (sessionId, duration = 30) => {
    try {
      await apiClient.post(`/tele-sessions/${sessionId}/end?duration_minutes=${duration}`);
      setSessions(sessions.map(s => 
        s.id === sessionId ? { ...s, status: "completed", duration_minutes: duration } : s
      ));
      setActiveSession(null);
      toast.success("Session ended!", {
        description: "Post-consultation follow-up has been scheduled"
      });
    } catch (error) {
      toast.error("Failed to end session");
    }
  };

  const copyJoinLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Join link copied!");
  };

  const getPatientName = (patientId) => {
    const patient = patients.find(p => p.id === patientId);
    return patient?.name || "Unknown Patient";
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: "bg-blue-100 text-blue-700",
      in_progress: "bg-green-100 text-green-700",
      completed: "bg-slate-100 text-slate-700",
      cancelled: "bg-red-100 text-red-700"
    };
    return <Badge className={styles[status] || styles.scheduled}>{status.replace("_", " ")}</Badge>;
  };

  const sessionTypes = [
    { value: "consultation", label: "Initial Consultation" },
    { value: "follow_up", label: "Follow Up" },
    { value: "fitting_check", label: "Fitting Check" },
    { value: "adjustment", label: "Remote Adjustment" },
    { value: "counseling", label: "Counseling" }
  ];

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="tele-audiology-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
              <Video className="w-7 h-7 text-sky-600" />
              Tele-Audiology
            </h1>
            <p className="text-slate-500">Remote hearing consultations and follow-ups</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-sky-600 hover:bg-sky-700" data-testid="schedule-tele-session-btn">
                <Plus className="w-4 h-4 mr-2" />
                Schedule Session
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Schedule Tele-Audiology Session</DialogTitle>
                <DialogDescription>Book a remote consultation with a patient</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateSession} className="space-y-4 mt-4">
                <div>
                  <Label>Patient *</Label>
                  <Select 
                    value={newSession.patient_id} 
                    onValueChange={(v) => setNewSession({...newSession, patient_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select patient" />
                    </SelectTrigger>
                    <SelectContent>
                      {patients.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name} ({p.phone})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Session Type *</Label>
                  <Select 
                    value={newSession.session_type} 
                    onValueChange={(v) => setNewSession({...newSession, session_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sessionTypes.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date & Time *</Label>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    className="rounded-md border mt-2"
                  />
                </div>
                <div>
                  <Label>Notes for Patient</Label>
                  <Textarea
                    value={newSession.notes}
                    onChange={(e) => setNewSession({...newSession, notes: e.target.value})}
                    placeholder="Preparation instructions, what to have ready..."
                    rows={3}
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    className="bg-sky-600 hover:bg-sky-700"
                    disabled={!newSession.patient_id}
                  >
                    Schedule Session
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: "Scheduled", status: "scheduled", icon: CalendarIcon, color: "bg-blue-100 text-blue-600" },
            { label: "In Progress", status: "in_progress", icon: Play, color: "bg-green-100 text-green-600" },
            { label: "Completed", status: "completed", icon: CheckCircle, color: "bg-slate-100 text-slate-600" },
            { label: "Total Sessions", status: null, icon: Video, color: "bg-sky-100 text-sky-600" }
          ].map((stat, idx) => {
            const count = stat.status 
              ? sessions.filter(s => s.status === stat.status).length
              : sessions.length;
            return (
              <Card key={idx}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color}`}>
                    <stat.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{count}</p>
                    <p className="text-sm text-slate-500">{stat.label}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Active Session Banner */}
        {activeSession && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                    <Video className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-green-900">Session In Progress</p>
                    <p className="text-sm text-green-700">
                      {getPatientName(activeSession.patient_id)} - {activeSession.session_type.replace("_", " ")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    onClick={() => window.open(activeSession.join_url, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Rejoin
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => endSession(activeSession.id, 30)}
                  >
                    <Square className="w-4 h-4 mr-2" />
                    End Session
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sessions List */}
        <Card>
          <CardHeader>
            <CardTitle>All Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="skeleton h-24 rounded-lg"></div>
                ))}
              </div>
            ) : sessions.length > 0 ? (
              <div className="space-y-4">
                {sessions.map(session => (
                  <div 
                    key={session.id}
                    className={`p-4 border rounded-lg transition-colors ${
                      session.status === "in_progress" 
                        ? "border-green-300 bg-green-50" 
                        : "border-slate-200 hover:bg-slate-50"
                    }`}
                    data-testid={`tele-session-${session.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          session.status === "in_progress" ? "bg-green-500" : "bg-sky-100"
                        }`}>
                          <Video className={`w-6 h-6 ${
                            session.status === "in_progress" ? "text-white" : "text-sky-600"
                          }`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-slate-900">
                              {getPatientName(session.patient_id)}
                            </p>
                            {getStatusBadge(session.status)}
                          </div>
                          <p className="text-sm text-slate-500 capitalize">
                            {session.session_type.replace("_", " ")}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="w-4 h-4" />
                              {format(new Date(session.scheduled_at), "PPP")}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {format(new Date(session.scheduled_at), "p")}
                            </span>
                            {session.duration_minutes && (
                              <span className="text-slate-400">
                                Duration: {session.duration_minutes} min
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {session.status === "scheduled" && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => copyJoinLink(session.join_url)}
                            >
                              <Copy className="w-4 h-4 mr-1" />
                              Copy Link
                            </Button>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => startSession(session.id)}
                            >
                              <Play className="w-4 h-4 mr-1" />
                              Start
                            </Button>
                          </>
                        )}
                        {session.status === "in_progress" && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(session.join_url, '_blank')}
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              Join
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => endSession(session.id, 30)}
                            >
                              <Square className="w-4 h-4 mr-1" />
                              End
                            </Button>
                          </>
                        )}
                        {session.status === "completed" && (
                          <Badge variant="outline" className="text-slate-500">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Completed
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Join URL Info */}
                    {session.status !== "completed" && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <p className="text-xs text-slate-400 font-mono truncate">
                          Room: {session.room_id}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Video className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No tele-audiology sessions</p>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => setDialogOpen(true)}
                >
                  Schedule your first session
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* How It Works - Jitsi Meet Integration */}
        <Card className="bg-gradient-to-r from-sky-50 to-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              How Tele-Audiology Works
              <Badge variant="outline" className="ml-2 text-xs">Powered by Jitsi Meet</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { step: 1, title: "Schedule", desc: "Book a session with the patient" },
                { step: 2, title: "Share Link", desc: "Patient receives Jitsi link via WhatsApp" },
                { step: 3, title: "Connect", desc: "Start secure video consultation" },
                { step: 4, title: "Follow-Up", desc: "Auto follow-up messages sent" }
              ].map((item) => (
                <div key={item.step} className="text-center p-4 bg-white rounded-lg">
                  <div className="w-10 h-10 bg-sky-600 text-white rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
                    {item.step}
                  </div>
                  <p className="font-semibold text-slate-900">{item.title}</p>
                  <p className="text-sm text-slate-500 mt-1">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-white rounded-lg border border-sky-200">
              <p className="text-sm text-slate-600 text-center">
                🔒 <strong>Secure & Free:</strong> Video calls are powered by Jitsi Meet - an open-source, encrypted video platform. No account needed for patients.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
